import { SearchContentType, SearchResultData } from '@novaheal/types';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch/lite';

// Get config values from environment or config file
const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APPLICATION_ID || '';
const ALGOLIA_API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY || '';
const ALGOLIA_INDEX_NAME = process.env.REACT_APP_ALGOLIA_INDEX_NAME || '';

const client: SearchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY);
const index: SearchIndex = client.initIndex(ALGOLIA_INDEX_NAME);

const ALGOLIA_BATCH_SIZE = 12;

type AlgoliaPageResult = {
  hits: SearchResultData[];
  nbHits: number;
  page: number;
  nbPages: number;
  typeCounts?: Record<string, number>;
};

const fetchAlgoliaContentPage = async ({
  page = 0,
  query = '',
  contentType,
  batchSize = ALGOLIA_BATCH_SIZE,
}: {
  page: number;
  query: string;
  contentType: SearchContentType[];
  batchSize?: number;
}): Promise<AlgoliaPageResult> => {
  try {
    // Handle "All" content type with multiple queries
    if (contentType.length === 0) {
      const contentTypes = [
        SearchContentType.ARTICLE,
        SearchContentType.MEDICATION,
        SearchContentType.IMAGE,
        SearchContentType.VIDEO,
      ];

      const searchRequests = contentTypes.map((type) => ({
        indexName: ALGOLIA_INDEX_NAME,
        query,
        params: {
          hitsPerPage: batchSize,
          page: Math.floor(page),
          filters: `contentType:"${type}"`,
        },
      }));

      const results = await client.search(searchRequests);

      // Combine all results
      const allHits: SearchResultData[] = [];
      const typeCounts: Record<string, number> = {
        articles: 0,
        medications: 0,
        media: 0,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      results.results.forEach((result: any, index: number) => {
        const contentTypeKey = contentTypes[index];
        allHits.push(...result.hits);

        // Map content types to categories for typeCounts
        if (contentTypeKey === SearchContentType.ARTICLE) {
          typeCounts.articles = result.nbHits;
        } else if (contentTypeKey === SearchContentType.MEDICATION) {
          typeCounts.medications = result.nbHits;
        } else if (
          contentTypeKey === SearchContentType.IMAGE ||
          contentTypeKey === SearchContentType.VIDEO
        ) {
          typeCounts.media += result.nbHits;
        }
      });

      const totalNbHits = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);

      return {
        hits: allHits,
        nbHits: totalNbHits,
        page: Math.floor(page),
        nbPages: Math.ceil(totalNbHits / batchSize),
        typeCounts,
      };
    }

    // Handle specific content types (original logic)
    const params: {
      hitsPerPage: number;
      page: number;
      filters?: string;
    } = {
      hitsPerPage: batchSize,
      page: Math.floor(page),
    };

    // Only add filters if contentType is not 'all'
    if (contentType.length > 0) {
      params.filters = `contentType:"${contentType.join('" OR contentType:"')}"`;
    }

    const results = await index.search<SearchResultData>(query, params);

    return {
      hits: results.hits,
      nbHits: results.nbHits,
      page: results.page,
      nbPages: results.nbPages,
    };
  } catch (error) {
    // Log error to console for debugging in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Algolia search error:', error);
    }
    return { hits: [], nbHits: 0, page: 0, nbPages: 0 };
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAlgoliaContent = (
  contentType: SearchContentType[],
  query: string,
  page = 1,
  disabled = false,
  batchSize = ALGOLIA_BATCH_SIZE
): UseQueryResult<AlgoliaPageResult, unknown> => {
  return useQuery({
    queryKey: [`algolia-content-${contentType.join(',')}`, query, page, batchSize],
    queryFn: () =>
      fetchAlgoliaContentPage({
        page: page - 1, // Convert to 0-based indexing
        query,
        contentType,
        batchSize,
      }),
    enabled: !!query && !!contentType && !disabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};
